import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { PaymentScenarionConditionFormInterface } from '@zaoblako/shared/modules/task/dtos/payment-scenarion/payment-scenarion-condition-form.dto';
import { PaymentScenarionFindInterface } from '@zaoblako/shared/modules/task/dtos/payment-scenarion/payment-scenarion-find.dto';
import {
  PaymentScenarionFormDto,
  PaymentScenarionFormInterface
} from '@zaoblako/shared/modules/task/dtos/payment-scenarion/payment-scenarion-form.dto';
import { PaymentScenarionActionMethodEnum } from '@zaoblako/shared/modules/task/enum/payment.scenarion.action.method.enum';
import { PaymentScenarionConditionKindEnum } from '@zaoblako/shared/modules/task/enum/payment.scenarion.condition.kind.enum';
import { PaymentScenarionConditionTypeEnum } from '@zaoblako/shared/modules/task/enum/payment.scenarion.condition.type.enum';
import { CompanyService } from '@zaoblako/web/core/services/client/company.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { DirectionService } from '@zaoblako/web/core/services/finance/direction.service';
import { PaymentTaskScenarioService } from '@zaoblako/web/core/services/finance/scenario/payment-task-scenario.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { UserService } from '@zaoblako/web/core/services/security/user.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { CostCenterMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-multi-select/cost-center-multi-select.component';
import { DealMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/deal-multi-select/deal-multi-select.component';
import { DirectionMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-multi-select/direction-multi-select.component';
import { FilialMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-multi-select/filial-multi-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { PaymentTaskScenarioConditionKindSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/payment-task-scenario-condition-kind-single-select/payment-task-scenario-condition-kind-single-select.component';
import { PaymentTaskScenarioConditionTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/payment-task-scenario-condition-type-single-select/payment-task-scenario-condition-type-single-select.component';
import { PaymentTaskScenarioMethodSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/payment-task-scenario-method-single-select/payment-task-scenario-method-single-select.component';
import { UnitMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-multi-select/unit-multi-select.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { TaskAnalyticsModeEnum } from '@zaoblako/shared/modules/task/enum/task-analytics-mode.enum';
import { TaskAnalyticsKindEnum } from '@zaoblako/shared/modules/task/enum/task-analytics-kind.enum';
import { JoinWithChildPipe } from '@zaoblako/web/shared/pipes/join-with-child.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { SkeletonModule } from 'primeng/skeleton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TagModule } from 'primeng/tag';
import { forkJoin, Observable } from 'rxjs';

@Component({
  standalone: true,
  selector: 'crm-task-scenario-form',
  templateUrl: './task-scenario-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    SpinnerComponent,
    ButtonModule,
    InputTextComponent,
    JoinPipe,
    SkeletonModule,
    DividerModule,
    PaymentTaskScenarioConditionKindSingleSelectComponent,
    PaymentTaskScenarioConditionTypeSingleSelectComponent,
    DirectionMultiSelectComponent,
    UnitMultiSelectComponent,
    FilialMultiSelectComponent,
    MultiSelectComponent,
    InputNumberModule,
    FormsModule,
    DealMultiSelectComponent,
    CostCenterMultiSelectComponent,
    PaymentTaskScenarioMethodSingleSelectComponent,
    TagModule,
    JoinWithChildPipe,
    SelectButtonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskScenarioFormComponent extends BaseFormComponent<PaymentScenarionFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public engine: 'class-transformer' | 'serde' = 'serde';
  public dto = PaymentScenarionFormDto;

  public scenario = <PaymentScenarionFormInterface>{
    name: '',
    paymentScenarionAction: {
      method: null,
      approvers: []
    },
    conditions: []
  };

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public directions: DirectionFindInterface[] = [];
  public companies: CompanyFindInterface[] = [];
  public users: UserFindInterface[] = [];

  public exceedingPlanAnalyticsKindOptions: { label: string; value: TaskAnalyticsKindEnum; icon: string }[] = [
    { label: 'БС', value: TaskAnalyticsKindEnum.BY_DIRECTION, icon: 'pi pi-tag' },
    { label: 'Группа БС', value: TaskAnalyticsKindEnum.BY_DIRECTION_GROUP, icon: 'pi pi-tags' }
  ];

  public exceedingPlanAnalyticsModeOptions: { label: string; value: TaskAnalyticsModeEnum; icon: string }[] = [
    { label: 'по ОПиУ/ДДС', value: TaskAnalyticsModeEnum.BY_BALANCE_REPORT, icon: 'pi pi-chart-bar' },
    { label: 'по платежному календарю', value: TaskAnalyticsModeEnum.BY_PAYMENT_CALENDAR, icon: 'pi pi-dollar' }
  ];

  public get PaymentScenarionConditionKindEnum() {
    return PaymentScenarionConditionKindEnum;
  }

  public get PaymentScenarionActionMethodEnum() {
    return PaymentScenarionActionMethodEnum;
  }

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly paymentTaskScenarioService: PaymentTaskScenarioService,
    private readonly directionService: DirectionService,
    private readonly companyService: CompanyService,
    private readonly confirmService: ConfirmDialogService,
    private readonly errorService: ErrorService,
    private readonly userService: UserService,
    private readonly cdRef: ChangeDetectorRef
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    forkJoin([
      this.directionService.find<DirectionFindInterface>(),
      this.companyService.find<CompanyFindInterface>(),
      this.userService.findAll()
    ]).subscribe(([directions, companies, users]) => {
      this.companies = companies;
      this.directions = directions;
      this.users = users;
      this.cdRef.markForCheck();
    });

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    }
  }

  public getById(): void {
    this.formStoreService.setLoading(true, this.index);

    this.paymentTaskScenarioService
      .findById<PaymentScenarionFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (scenario) => {
          this.scenario = scenario as any;
          this.scenario.paymentScenarionAction.approvers = this.scenario.paymentScenarionAction.approvers?.map(
            (user) => user?.['approver']
          );
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveScenario(): void {
    if (this.checkValidate(this.scenario).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.paymentTaskScenarioService
          .update<PaymentScenarionFormInterface, PaymentScenarionFindInterface>(this.scenario)
          .subscribe({
            next: (scenario) => {
              this.formStoreService.setLoading(false, this.index);
              this.errorService.addSuccessMessage(`Сценарий "${scenario?.name}" изменен!`);
              this.formStoreService.saveNext(scenario, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.paymentTaskScenarioService.create<PaymentScenarionFormInterface>(this.scenario).subscribe({
          next: (scenario) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Сценарий создан!');
            this.formStoreService.saveNext(scenario, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteScenario(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить сценарий "${this.scenario.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.paymentTaskScenarioService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.errorService.addSuccessMessage(`Сценарий "${this.scenario?.name}" удален!`);
              this.formStoreService.deleteNext(this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public addCondition() {
    if (!this.scenario.conditions) {
      this.scenario.conditions = [];
    }

    const condition: PaymentScenarionConditionFormInterface = {
      paymentScenarion: <PaymentScenarionFindInterface>{},
      kind: null,
      type: null
    };
    this.scenario.conditions.push(condition);
  }

  public deleteCondition(condition: PaymentScenarionConditionFormInterface) {
    this.scenario.conditions = this.scenario.conditions.filter((_condition) => _condition !== condition);
    this.scenario.conditions = [...this.scenario.conditions];
  }

  public restoreScenario(): void {
    this.confirmService.confirm({
      message: `Вы действительно хотите восстановить сценарий "${this.scenario?.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.paymentTaskScenarioService
          .restoreOne(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe({
            next: () => {
              this.scenario = { ...this.scenario, deleted: null } as any;
              this.cdRef.markForCheck();
              this.errorService.addSuccessMessage(`Сценарий "${this.scenario?.name}" восстановлен!`);
              this.formStoreService.saveNext(this.scenario, this.index);
            },
            error: () => {
              this.formStoreService.setLoading(false, this.index);
            }
          });
      }
    });
  }

  public setConditionOptions(condition: PaymentScenarionConditionFormInterface): void {
    if (condition.kind === PaymentScenarionConditionKindEnum.EXCEEDING_PLAN) {
      [condition.type, condition.excceedingPlanAnalyticsMode, condition.excceedingPlanAnalyticsKind] = [
        PaymentScenarionConditionTypeEnum.NONE,
        TaskAnalyticsModeEnum.BY_BALANCE_REPORT,
        TaskAnalyticsKindEnum.BY_DIRECTION
      ];
      this.cdRef.markForCheck();
    }
  }
}
